// Claiming your GG Bharat name: the name (on its pass, checked as you type), your email and phone, where you're from,
// then the ₹49 fee through Razorpay's checkout, and your numbered founder pass to keep or share, with a free account
// made on the spot from the same email. Before Razorpay's keys are on the server, the last step reserves the name to
// pay for later (the server says which). Opened from anywhere with openRegister(name?).
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { STATES, checkHandle, checkPhone } from '../../shared/handle';
import { PASSWORD_MIN, validateEmail } from '../../shared/account';
import { refreshAccount, signUp, useAccount } from './account';
import { BUSINESS, PASS, REG } from './brand';
import { Pass, PassField } from './Pass';
import { useNameCheck } from './Plate';

// ───────────── what the server says (payments on? the fee, how many) ─────────────

export interface RegConfig {
  payments: boolean;
  keyId: string | null;
  fee: number;
  count: number;
}
let config: RegConfig | null = null;
const cfgListeners = new Set<(c: RegConfig) => void>();
let asked = false;
export function useRegConfig() {
  const [c, setC] = useState(config);
  useEffect(() => {
    cfgListeners.add(setC);
    if (!asked) {
      asked = true;
      fetch('/api/register/config')
        .then((r) => (r.ok ? r.json() : null))
        .then((j: RegConfig | null) => {
          if (!j) return;
          config = j;
          for (const l of cfgListeners) l(j);
        })
        .catch(() => {});
    }
    return () => {
      cfgListeners.delete(setC);
    };
  }, []);
  return c;
}

// ───────────── opening it from anywhere ─────────────

const openers = new Set<(name: string) => void>();
export function openRegister(name = '') {
  for (const o of openers) o(name);
}

/** done on this device: shown instead of the form next time */
const DONE_KEY = 'gg.registered';
export interface Registered {
  name: string;
  founder: number | null;
  paid: boolean;
}
export function loadRegistered(): Registered | null {
  try {
    return JSON.parse(localStorage.getItem(DONE_KEY) ?? 'null');
  } catch {
    return null;
  }
}
function saveRegistered(r: Registered) {
  try {
    localStorage.setItem(DONE_KEY, JSON.stringify(r));
  } catch {
    /* it just won't be remembered */
  }
}

// ───────────── Razorpay's checkout ─────────────

declare global {
  interface Window {
    Razorpay?: new (o: Record<string, unknown>) => { open: () => void; on: (ev: string, fn: (r: unknown) => void) => void };
  }
}
let rzp: Promise<void> | null = null;
const loadRazorpay = () =>
  (rzp ??= new Promise<void>((ok, fail) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => ok();
    s.onerror = () => {
      rzp = null;
      fail(new Error('Couldn’t load the payment window — check your connection.'));
    };
    document.head.appendChild(s);
  }));

// ───────────── the form ─────────────

const STEPS = ['Name', 'Contact', 'Place', 'Fee', 'Done'] as const;
const CITIES = ['Chandigarh', 'Mohali', 'Panchkula', 'Zirakpur', 'Kharar', 'Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Ambala', 'Delhi', 'Gurugram', 'Noida', 'Shimla', 'Dehradun', 'Jaipur', 'Mumbai', 'Bengaluru', 'Pune', 'Hyderabad', 'Kolkata', 'Chennai', 'Ahmedabad', 'Lucknow'];

type Step = 0 | 1 | 2 | 3 | 4;

export function RegisterHost() {
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState('');
  useEffect(() => {
    const o = (n: string) => {
      setSeed(n);
      setOpen(true);
    };
    openers.add(o);
    return () => {
      openers.delete(o);
    };
  }, []);
  if (!open) return null;
  return <RegisterDialog seed={seed} onClose={() => setOpen(false)} />;
}

function RegisterDialog({ seed, onClose }: { seed: string; onClose: () => void }) {
  const cfg = useRegConfig();
  const before = loadRegistered();
  const [step, setStep] = useState<Step>(before ? 4 : 0);
  const [dir, setDir] = useState<1 | -1>(1);
  const [name, setName] = useState(before?.name ?? seed);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [state, setState] = useState('Chandigarh');
  const [city, setCity] = useState('');
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Registered | null>(before);
  const check = useNameCheck(step === 0 ? name : '', undefined);
  const card = useRef<HTMLDivElement>(null);
  const me = useAccount();
  // logged in: your account's email is the one to join with
  useEffect(() => {
    if (me && !email) setEmail(me.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose();
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [onClose, busy]);
  useEffect(() => {
    card.current?.querySelector<HTMLElement>('input, select, button.gx-btn')?.focus({ preventScroll: true });
  }, [step]);

  const go = (s: Step) => {
    setDir(s > step ? 1 : -1);
    setErr(null);
    setStep(s);
  };
  const fee = (cfg?.fee ?? REG.fee * 100) / 100;

  const next = async () => {
    setErr(null);
    if (step === 0) {
      const h = checkHandle(name);
      if (!h.ok) return setErr(h.error);
      if (check.status === 'bad') return setErr(check.message);
      if (check.status !== 'ok') return setErr('One moment: still checking the name.');
      return go(1);
    }
    if (step === 1) {
      const e = validateEmail(email);
      if (!e.ok) return setErr(e.error);
      const p = checkPhone(phone);
      if (!p.ok) return setErr(p.error);
      return go(2);
    }
    if (step === 2) {
      if (city.trim().length < 2) return setErr('Enter your city or town.');
      return go(3);
    }
    if (step === 3) {
      if (!agree) return setErr('Please agree to the terms and the refund policy.');
      setBusy(true);
      try {
        const r = await fetch('/api/register/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, phone, state, city, source: 'site' }),
        });
        const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
        if (!r.ok) {
          setBusy(false);
          setErr(String(j.error ?? 'Something went wrong — try again.'));
          if (j.field === 'name') go(0);
          else if (j.field === 'email' || j.field === 'phone') go(1);
          else if (j.field === 'place') go(2);
          return;
        }
        if (j.status === 'requested' || j.status === 'already') return finish({ name: String(j.name), founder: (j.founder as number) ?? null, paid: j.paid === true });
        if (j.status === 'checkout') return pay(j);
        setErr('Something went wrong — try again.');
      } catch {
        setErr('Can’t reach the server — check your connection and try again.');
      }
      setBusy(false);
    }
  };

  const pay = async (j: Record<string, unknown>) => {
    try {
      await loadRazorpay();
    } catch (e) {
      setBusy(false);
      return setErr((e as Error).message);
    }
    const R = window.Razorpay!;
    const rz = new R({
      key: j.keyId,
      amount: j.amount,
      currency: 'INR',
      name: BUSINESS.brand,
      description: `GG Bharat name registration: ${j.name}`,
      order_id: j.order,
      prefill: { email: j.email, contact: j.phone },
      notes: { name: j.name },
      theme: { color: '#ff8a2a' },
      modal: {
        ondismiss: () => {
          setBusy(false);
          setErr('Payment not finished. Your name is held for a few minutes — try again.');
        },
      },
      handler: async (resp: Record<string, string>) => {
        try {
          const v = await fetch('/api/register/verify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(resp) });
          const k = (await v.json().catch(() => ({}))) as Record<string, unknown>;
          if (k.status === 'paid') return finish({ name: String(k.name), founder: (k.founder as number) ?? null, paid: true });
          setBusy(false);
          setErr(
            k.status === 'conflict'
              ? `Your payment went through, but “${k.name}” was taken just before. We’ll write to you within a day to pick another name or refund you.`
              : String(k.error ?? 'We couldn’t confirm the payment. If money left your account, write to us and we’ll sort it out.'),
          );
        } catch {
          setBusy(false);
          setErr('We couldn’t confirm the payment just now. If money left your account, it will be confirmed shortly — or write to us.');
        }
      },
    });
    rz.on('payment.failed', () => {
      setBusy(false);
      setErr('The payment didn’t go through. Nothing was charged — try again or use another method.');
    });
    rz.open();
  };

  const finish = (r: Registered) => {
    setBusy(false);
    saveRegistered(r);
    setDone(r);
    go(4);
    // logged in: the account now shows its place on the waitlist (and goes by the name)
    if (me) void refreshAccount();
  };

  return createPortal(
    <div className="gx-modal" role="dialog" aria-modal="true" aria-label="Join the waitlist" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="gx-form20" ref={card}>
        <header>
          <div>
            <span className="mono">GG Bharat · The waitlist</span>
            <h2>{step === 4 ? 'You’re on the waitlist.' : 'Join the waitlist'}</h2>
          </div>
          <button type="button" className="x" onClick={onClose} aria-label="Close" disabled={busy}>
            ×
          </button>
        </header>
        <ol className="gx-steps" aria-label="Steps">
          {STEPS.map((s, i) => (
            <li key={s} className={i < step ? 'done' : i === step ? 'on' : ''} aria-current={i === step ? 'step' : undefined}>
              <b className="mono">{String(i + 1).padStart(2, '0')}</b>
              {s}
            </li>
          ))}
        </ol>
        <div className={`gx-stepbody d${dir}`} key={step}>
          {step === 0 && (
            <>
              <p className="lede">Joining reserves your GG Bharat name, for good: the games, the shows, the events. Nobody else can have it: first come, first served.</p>
              <PassField value={name} onChange={setName} check={check} autoFocus />
            </>
          )}
          {step === 1 && (
            <>
              <p className="lede">Where we tell you the game is open, and send your receipt. Never shown to other players.</p>
              <label className="gx-lbl">
                Email
                <input className="gx-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" maxLength={254} />
              </label>
              <label className="gx-lbl">
                Mobile number
                <div className="gx-phone">
                  <span className="mono">+91</span>
                  <input className="gx-input" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98765 43210" autoComplete="tel-national" maxLength={18} />
                </div>
              </label>
            </>
          )}
          {step === 2 && (
            <>
              <p className="lede">Where are you from? It helps us choose which city to build next.</p>
              <label className="gx-lbl">
                State / UT
                <select className="gx-input" value={state} onChange={(e) => setState(e.target.value)}>
                  {STATES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label className="gx-lbl">
                City or town
                <input className="gx-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Mohali" list="gx-cities" autoComplete="address-level2" maxLength={40} />
                <datalist id="gx-cities">
                  {CITIES.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </label>
            </>
          )}
          {step === 3 && (
            <>
              <div className="gx-receipt sm">
                <div className="r-head mono">
                  <b>REGISTRATION FEE</b>
                  <span>{new Date().toLocaleDateString('en-IN')}</span>
                </div>
                <Pass name={name} size="sm" />
                <dl className="mono">
                  <div>
                    <dt>Name</dt>
                    <dd>{name}</dd>
                  </div>
                  <div>
                    <dt>Contact</dt>
                    <dd>{email}</dd>
                  </div>
                  <div>
                    <dt>From</dt>
                    <dd>
                      {city}, {state}
                    </dd>
                  </div>
                  <div className="total">
                    <dt>Total (incl. taxes)</dt>
                    <dd>₹{fee}</dd>
                  </div>
                </dl>
              </div>
              <label className="gx-check">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                <span>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener">
                    terms
                  </a>{' '}
                  and the{' '}
                  <a href="/refunds" target="_blank" rel="noopener">
                    refund policy
                  </a>{' '}
                  ({REG.refundDays}-day refund).
                </span>
              </label>
              {cfg && !cfg.payments && <p className="gx-note">Payments aren’t open yet. Reserve your name now: when they open, we’ll email you a link to pay the ₹{fee}. Until then the name is held for you.</p>}
            </>
          )}
          {step === 4 && done && <Certificate r={done} email={before ? '' : email} />}
          {err && (
            <p className="gx-err" role="alert">
              {err}
            </p>
          )}
        </div>
        {step < 4 && (
          <footer>
            {step > 0 ? (
              <button type="button" className="gx-btn ghost" onClick={() => go((step - 1) as Step)} disabled={busy}>
                ← Back
              </button>
            ) : (
              <span />
            )}
            <button type="button" className="gx-btn" onClick={() => void next()} disabled={busy || (step === 0 && check.status === 'checking')}>
              {busy ? 'One moment…' : step === 3 ? (cfg && !cfg.payments ? 'Reserve my name' : `Pay ₹${fee}`) : 'Next'}
              <span aria-hidden>→</span>
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ───────────── your founder pass, and your account ─────────────

/** right after claiming: a password is all it takes to make the free account (same email, the name comes with it) */
function MakeAccount({ email, name }: { email: string; name: string }) {
  const me = useAccount();
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [made, setMade] = useState(false);
  if (me) return <p className="gx-note ok">You’re logged in as {me.email}: your name is on your account.</p>;
  if (made) return <p className="gx-note ok">Account made. On launch day, just log in with {email}.</p>;
  if (!email)
    return (
      <p className="gx-note">
        Make your free account with the email you claimed with, and the name comes with it: <a href="/login?mode=signup">create account</a>.
      </p>
    );
  const go = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw.length < PASSWORD_MIN) return setErr(`Use at least ${PASSWORD_MIN} characters.`);
    setBusy(true);
    setErr(null);
    const r = await signUp({ email, password: pw, displayName: name, instagram: '' });
    setBusy(false);
    if (r.error) return setErr(r.error);
    setMade(true);
  };
  return (
    <form className="gx-acct" onSubmit={go}>
      <p>
        <b>Now make your free account.</b> Same email ({email}), the name comes with it. On launch day you just log in.
      </p>
      <div className="a-row">
        <label className="sr" htmlFor="gx-acct-pw">
          Choose a password
        </label>
        <input id="gx-acct-pw" className="gx-input" type={show ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} placeholder={`A password, ${PASSWORD_MIN}+ characters`} autoComplete="new-password" minLength={PASSWORD_MIN} maxLength={200} />
        <button type="button" className="eye mono" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}>
          {show ? 'Hide' : 'Show'}
        </button>
        <button type="submit" className="gx-btn" disabled={busy}>
          {busy ? 'One moment…' : 'Make account'}
        </button>
      </div>
      {err && (
        <p className="gx-err" role="alert">
          {err}
        </p>
      )}
    </form>
  );
}

function Certificate({ r, email }: { r: Registered; email: string }) {
  const me = useAccount();
  const no = r.founder ? String(r.founder).padStart(4, '0') : '----';
  const date = useMemo(() => new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }), []);
  const save = async () => {
    const png = await drawCertificate(r.name, no, date, r.paid);
    const a = document.createElement('a');
    a.href = png;
    a.download = `ggbharat-${r.name}.png`;
    a.click();
  };
  const share = async () => {
    const text = `I just claimed @${r.name} on GG Bharat: founder #${no}. First stop, Chandigarh: The City Beautiful.`;
    try {
      if (navigator.share) await navigator.share({ title: 'GG Bharat', text, url: 'https://ggbharat.com' });
      else {
        await navigator.clipboard.writeText(`${text} https://ggbharat.com`);
        alert('Copied: paste it anywhere.');
      }
    } catch {
      /* cancelled */
    }
  };
  return (
    <div className="gx-cert">
      <Pass name={r.name} founder={r.founder} size="md" stamp={r.paid ? 'Registered' : 'Reserved'} />
      <p className="sub">
        {r.paid ? 'Fee received: the name is registered to you, for good.' : 'Held for you until payments open: we’ll email you the link to pay.'} <span className="mono">{date}</span>
      </p>
      <div className="acts">
        <button type="button" className="gx-btn" onClick={() => void save()}>
          Download your pass
        </button>
        <button type="button" className="gx-btn ghost" onClick={() => void share()}>
          Share
        </button>
      </div>
      <MakeAccount email={email} name={r.name} />
      <p className="gx-note">
        Founders get in first when Chandigarh opens: we’ll email you the day. The {PASS.name} (₹{PASS.price}) goes on sale then.{' '}
        {me ? <a href="/account">See your account</a> : null}
      </p>
    </div>
  );
}

/** the Konark wheel on a canvas: rim, beads, eight broad spokes with medallions, eight slim ones, the hub */
function drawWheel(g: CanvasRenderingContext2D, x: number, y: number, r: number, ink: string, cut: string) {
  const k = r / 100;
  g.save();
  g.translate(x, y);
  g.fillStyle = ink;
  g.beginPath();
  g.arc(0, 0, 98 * k, 0, Math.PI * 2);
  g.arc(0, 0, 84 * k, 0, Math.PI * 2, true);
  g.fill();
  for (let i = 0; i < 16; i++) {
    const a = (i * Math.PI) / 8 - Math.PI / 2;
    const w = (i % 2 ? 2.3 : 6.3) * k;
    g.save();
    g.rotate(a);
    g.fillRect(29 * k, -w, 52 * k, w * 2);
    g.beginPath();
    g.arc(56 * k, 0, (i % 2 ? 4 : 10.5) * k, 0, Math.PI * 2);
    g.fill();
    g.restore();
  }
  g.beginPath();
  g.arc(0, 0, 31 * k, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = cut;
  for (let i = 0; i < 40; i++) {
    const a = (i * Math.PI) / 20;
    g.beginPath();
    g.arc(Math.cos(a) * 91 * k, Math.sin(a) * 91 * k, 2.6 * k, 0, Math.PI * 2);
    g.fill();
  }
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4 - Math.PI / 2;
    g.beginPath();
    g.arc(Math.cos(a) * 56 * k, Math.sin(a) * 56 * k, 5.2 * k, 0, Math.PI * 2);
    g.fill();
  }
  g.beginPath();
  g.arc(0, 0, 9 * k, 0, Math.PI * 2);
  g.fill();
  g.restore();
}

/** your founder pass as a 1080×1350 picture (an Instagram post): the pass on paper, in a dusk sky above the clouds */
async function drawCertificate(name: string, no: string, date: string, paid: boolean) {
  await document.fonts.ready;
  const W = 1080;
  const H = 1350;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d')!;
  // the sky at dusk, a glow where the sun went down, a few clouds
  const sky = g.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, '#050302');
  sky.addColorStop(0.45, '#1f0f08');
  sky.addColorStop(0.78, '#6e2a10');
  sky.addColorStop(1, '#ff8a2a');
  g.fillStyle = sky;
  g.fillRect(0, 0, W, H);
  const glow = g.createRadialGradient(W / 2, H * 1.02, 10, W / 2, H * 1.02, H * 0.55);
  glow.addColorStop(0, 'rgba(255,170,90,.75)');
  glow.addColorStop(1, 'rgba(255,120,80,0)');
  g.fillStyle = glow;
  g.fillRect(0, 0, W, H);
  for (let i = 0; i < 70; i++) {
    const x = (i * 197) % W;
    const y = H * 0.82 + Math.sin(i * 1.7) * 60 + (i % 7) * 14;
    const r = 60 + ((i * 37) % 90);
    const cl = g.createRadialGradient(x, y, 0, x, y, r);
    cl.addColorStop(0, 'rgba(255,186,150,.28)');
    cl.addColorStop(1, 'rgba(255,186,150,0)');
    g.fillStyle = cl;
    g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  for (let i = 0; i < 90; i++) {
    g.fillStyle = `rgba(255,255,255,${0.25 + ((i * 13) % 10) / 20})`;
    g.fillRect((i * 389) % W, (i * 211) % (H * 0.5), 2, 2);
  }
  // the wordmark
  g.fillStyle = '#fff1dc';
  g.font = '900 96px "Anybody", "Arial Black", sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  const gg = g.measureText('GG').width;
  const bh = g.measureText('BHARAT').width;
  const gap = 30;
  const wr = 52;
  const total = gg + bh + wr * 2 + gap * 2;
  const x0 = (W - total) / 2;
  g.fillText('GG', x0 + gg / 2, 170);
  drawWheel(g, x0 + gg + gap + wr, 166, wr, '#ffb04a', 'rgba(70,20,30,.55)');
  g.fillText('BHARAT', x0 + gg + gap * 2 + wr * 2 + bh / 2, 170);
  // the pass
  const px = 110;
  const py = 360;
  const pw = W - 220;
  const ph = 560;
  g.save();
  g.shadowColor = 'rgba(0,0,0,.45)';
  g.shadowBlur = 50;
  g.shadowOffsetY = 24;
  g.fillStyle = '#f6ecd9';
  g.beginPath();
  g.roundRect(px, py, pw, ph, 26);
  g.fill();
  g.restore();
  // the stub and its perforation
  g.fillStyle = '#ead9bb';
  g.beginPath();
  g.roundRect(px, py, 190, ph, [26, 0, 0, 26]);
  g.fill();
  g.fillStyle = '#2a1a12';
  for (let y = py + 20; y < py + ph - 10; y += 22) {
    g.beginPath();
    g.arc(px + 190, y, 4, 0, Math.PI * 2);
    g.fill();
  }
  drawWheel(g, px + 95, py + 140, 62, '#e2702a', 'rgba(246,236,217,.9)');
  g.save();
  g.translate(px + 95, py + 380);
  g.rotate(-Math.PI / 2);
  g.fillStyle = '#5a3a2a';
  g.font = '700 30px "JetBrains Mono", monospace';
  g.fillText('ADMIT ONE · EVERY WORLD', 0, 0);
  g.restore();
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
  g.fillStyle = '#5a3a2a';
  g.font = '700 26px "JetBrains Mono", monospace';
  g.fillText('GG BHARAT · NAME PASS', px + 240, py + 80);
  g.fillText(`FOUNDER #${no}`, px + 240, py + 120);
  g.fillStyle = '#2a1a12';
  let size = 120;
  const setFont = () => (g.font = `900 ${size}px "Anybody", "Arial Black", sans-serif`);
  setFont();
  while (g.measureText(`@${name}`).width > pw - 290 && size > 44) {
    size -= 6;
    setFont();
  }
  g.fillText(`@${name}`, px + 236, py + 300);
  g.fillStyle = '#6b4a35';
  g.font = '500 32px "Mukta", sans-serif';
  g.fillText('Valid in every game, show and event.', px + 240, py + 380);
  g.fillText('One of a kind, for good.', px + 240, py + 424);
  g.font = '400 24px "JetBrains Mono", monospace';
  g.fillText(date, px + 240, py + 506);
  // the stamp
  g.save();
  g.translate(px + pw - 170, py + ph - 110);
  g.rotate(-0.2);
  g.strokeStyle = paid ? '#1f7a4a' : '#c2412b';
  g.fillStyle = paid ? '#1f7a4a' : '#c2412b';
  g.lineWidth = 6;
  g.beginPath();
  g.roundRect(-150, -48, 300, 96, 10);
  g.stroke();
  g.font = '900 52px "Anybody", "Arial Black", sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(paid ? 'REGISTERED' : 'RESERVED', 0, 4);
  g.restore();
  // the way here
  g.fillStyle = '#fff1dc';
  g.font = '700 40px "Anybody", "Arial Black", sans-serif';
  g.textAlign = 'center';
  g.fillText('First stop: Chandigarh, the City Beautiful', W / 2, 1060);
  g.font = '400 30px "JetBrains Mono", monospace';
  g.fillStyle = 'rgba(255,241,220,.85)';
  g.fillText('ggbharat.com', W / 2, 1120);
  return c.toDataURL('image/png');
}
